import { Application, Container, Text, TextStyle, Ticker } from 'pixi.js';
import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';

export class Renderer {
  constructor() {
    this.app = null;
    this.layers = {};
    this._initialized = false;
    this._fpsText = null;
    this._resizeObserver = null;
    this._debugMode = new URLSearchParams(window.location.search).get('debug') !== null;
  }

  async init() {
    const canvas = document.getElementById('game-canvas');
    if (!canvas) {
      DebugLog.error('[boot] Renderer.init: #game-canvas not found in DOM');
      throw new Error('Renderer.init: #game-canvas element not found');
    }

    this.app = new Application();
    await this.app.init({
      canvas,
      width: window.innerWidth || 1280,
      height: window.innerHeight || 720,
      backgroundColor: 0x0a0808,
      antialias: false,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
      powerPreference: 'default',
    });

    // Layer 0: world — terrain, maps (camera transforms applied here)
    this.layers.world = new Container();
    this.layers.world.label = 'layer-world';

    // Layer 1: entities — party, NPCs, enemies
    this.layers.entities = new Container();
    this.layers.entities.label = 'layer-entities';

    // Layer 2: effects — particles, highlights, AoE indicators
    this.layers.effects = new Container();
    this.layers.effects.label = 'layer-effects';

    // Layer 3: hud — PixiJS HUD elements only (not HTML UI)
    this.layers.hud = new Container();
    this.layers.hud.label = 'layer-hud';

    this.app.stage.addChild(this.layers.world);
    this.app.stage.addChild(this.layers.entities);
    this.app.stage.addChild(this.layers.effects);
    this.app.stage.addChild(this.layers.hud);

    // FPS counter — only in debug mode
    if (this._debugMode) {
      this._initFpsCounter();
    }

    // ResizeObserver for responsive canvas sizing
    this._resizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    this._resizeObserver.observe(document.body);

    this._initialized = true;

    DebugLog.info(`[boot] Renderer initialized: ${this.width}x${this.height}`);
    return this;
  }

  _initFpsCounter() {
    const style = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 12,
      fill: 0x00ff88,
      align: 'left',
    });
    this._fpsText = new Text({ text: 'FPS: --', style });
    this._fpsText.x = 8;
    this._fpsText.y = 8;
    this._fpsText.zIndex = 9999;
    this.layers.hud.addChild(this._fpsText);

    let frameCount = 0;
    let lastTime = performance.now();

    this.app.ticker.add(() => {
      frameCount++;
      const now = performance.now();
      const delta = now - lastTime;
      if (delta >= 500) {
        const fps = Math.round((frameCount / delta) * 1000);
        const frameMs = (delta / frameCount).toFixed(1);
        this._fpsText.text = `FPS: ${fps}  frame: ${frameMs}ms`;
        frameCount = 0;
        lastTime = now;
      }
    });
  }

  resize() {
    if (!this.app || !this.app.renderer) return;

    const w = window.innerWidth;
    const h = window.innerHeight;

    this.app.renderer.resize(w, h);

    EventBus.emit('renderer:resize', { width: w, height: h });
  }

  get width() {
    return this.app?.renderer?.width ?? 0;
  }

  get height() {
    return this.app?.renderer?.height ?? 0;
  }

  get stage() {
    return this.app?.stage ?? null;
  }

  destroy() {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this.app?.destroy(false, { children: true });
    this.app = null;
    this._initialized = false;
    DebugLog.info('[boot] Renderer destroyed');
  }
}

export const renderer = new Renderer();
export default renderer;
